import { Reveal } from "@/components/Reveal";
import { motion } from "framer-motion";
import { Phone, MapPin, Mail, MessageCircle, Clock, Facebook } from "lucide-react";
import { CONTACT } from "@/constants/assets";

export const Contact = () => (
  <section id="contact" data-testid="contact-section" className="py-20 sm:py-28 bg-cream">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2rem] bg-navy p-6 sm:p-14 grain shadow-2xl shadow-navy/25">
          <div className="pointer-events-none absolute -top-20 -right-16 h-72 w-72 rounded-full bg-turquoise/25 blur-3xl" />
          <div className="relative grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-turquoise">
                Get in touch
              </p>
              <h2 className="mt-4 font-display fluid-h2 font-semibold text-white">
                Get in touch today for a{" "}
                <span className="italic text-turquoise">free quote</span>
              </h2>
              <p className="mt-5 max-w-md text-white/70 text-lg wrap-anywhere">
                Friendly, local and reliable carpet and upholstery cleaning
                across Airdrie, Lanarkshire and surrounding areas. Give us a
                call or message us on WhatsApp — we'll happily talk through
                what you need.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-4">
                <motion.a
                  href={CONTACT.phoneHref}
                  data-testid="contact-call-btn"
                  whileHover={{ y: -3 }}
                  className="inline-flex items-center justify-center gap-3 rounded-full bg-turquoise px-7 py-4 text-base font-bold text-white shadow-lg shadow-turquoise/30 transition-colors duration-300 hover:bg-turquoise-bright"
                >
                  <Phone className="h-5 w-5" />
                  Call {CONTACT.phone}
                </motion.a>
                <motion.a
                  href={CONTACT.whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn"
                  whileHover={{ y: -3 }}
                  className="inline-flex items-center justify-center gap-3 rounded-full bg-white px-7 py-4 text-base font-bold text-navy shadow-lg transition-colors duration-300 hover:bg-turquoise-soft"
                >
                  <MessageCircle className="h-5 w-5" />
                  WhatsApp
                </motion.a>
              </div>

              <a
                href={CONTACT.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-facebook-btn"
                className="mt-4 inline-flex items-center gap-3 rounded-full bg-white/10 px-7 py-4 text-base font-semibold text-white ring-1 ring-white/20 transition-all duration-300 hover:bg-white/20 hover:-translate-y-0.5"
              >
                <Facebook className="h-5 w-5" />
                Visit Our Facebook Page
              </a>
            </div>

            <div className="space-y-4">
              <a
                href={CONTACT.phoneHref}
                data-testid="contact-phone"
                className="group flex items-center gap-4 rounded-2xl bg-white/5 p-5 ring-1 ring-white/10 transition-colors duration-300 hover:bg-white/10"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-turquoise/20 text-turquoise">
                  <Phone className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs uppercase tracking-widest text-white/45">
                    Call us — the best way to reach us
                  </span>
                  <span className="block text-lg font-semibold text-white">
                    {CONTACT.phone}
                  </span>
                </span>
              </a>

              <a
                href={CONTACT.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="group flex items-center gap-4 rounded-2xl bg-white/5 p-5 ring-1 ring-white/10 transition-colors duration-300 hover:bg-white/10"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-turquoise/20 text-turquoise">
                  <MessageCircle className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs uppercase tracking-widest text-white/45">
                    WhatsApp
                  </span>
                  <span className="block text-lg font-semibold text-white">
                    {CONTACT.whatsapp}
                  </span>
                </span>
              </a>

              <a
                href={CONTACT.emailHref}
                data-testid="contact-email"
                className="group flex items-center gap-4 rounded-2xl bg-white/5 p-5 ring-1 ring-white/10 transition-colors duration-300 hover:bg-white/10"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-turquoise/20 text-turquoise">
                  <Mail className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs uppercase tracking-widest text-white/45">
                    Email
                  </span>
                  <span className="block wrap-anywhere text-base sm:text-lg font-semibold text-white">
                    {CONTACT.email}
                  </span>
                </span>
              </a>

              <div
                data-testid="contact-location"
                className="flex items-center gap-4 rounded-2xl bg-white/5 p-5 ring-1 ring-white/10"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-turquoise/20 text-turquoise">
                  <MapPin className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs uppercase tracking-widest text-white/45">
                    Based in
                  </span>
                  <span className="block text-lg font-semibold text-white">
                    {CONTACT.location}
                  </span>
                </span>
              </div>

              <div
                data-testid="contact-hours"
                className="flex items-center gap-4 rounded-2xl bg-white/5 p-5 ring-1 ring-white/10"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-turquoise/20 text-turquoise">
                  <Clock className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs uppercase tracking-widest text-white/45">
                    Opening hours
                  </span>
                  <span className="block text-lg font-semibold text-white">
                    {CONTACT.hours}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
